import { Injectable, effect, inject, signal, untracked } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { EMPTY, Observable, Subject, Subscription, timer } from 'rxjs';
import { catchError, exhaustMap, tap } from 'rxjs/operators';
import { environment } from '@env/environment';
import { AuthService } from '@features/auth/auth.service';
import { LanguageService } from '@core/services/language/language.service';
import {
  NotificationDTO,
  NotificationListResponse,
  NotificationPollResponse,
  NotificationPreference,
  defaultPreference,
  interpolate
} from './notification.model';

const POLL_INTERVAL_MS = 2500;
/** No repetir el "ding" más de una vez cada 2s aunque lleguen varios eventos de golpe. */
const MIN_MS_BETWEEN_DINGS = 2000;

/**
 * Estado global de notificaciones: polling cada 2,5s (solo con sesión iniciada
 * y pestaña visible), contador de no vistas, stream de eventos nuevos para los
 * toasts y caché de preferencias para decidir toast/sonido en cliente.
 */
@Injectable({ providedIn: 'root' })
export class NotificationsService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);
  private readonly language = inject(LanguageService);

  private readonly apiUrl = `${environment.baseApiUrl}/notifications`;

  /** Nº de notificaciones sin ver (burbujita de la campanita). */
  readonly unseenCount = signal(0);
  /** Último id conocido según el servidor. */
  readonly latestId = signal<number | null>(null);
  /** Se incrementa cada vez que el poll trae eventos nuevos (dispara la animación de la campanita). */
  readonly pulse = signal(0);
  /** Caché local de preferencias (null hasta la primera carga). */
  readonly preferences = signal<NotificationPreference[] | null>(null);

  /** Eventos nuevos (no vistos) que llegan por polling; lo consumen los toasts y el panel. */
  private readonly eventsSubject = new Subject<NotificationDTO>();
  readonly events$ = this.eventsSubject.asObservable();

  /** afterId del polling. Se mantiene aparte de la señal para no leer señales dentro del stream. */
  private lastPolledId: number | null = null;
  private pollSub: Subscription | null = null;
  private prefsRequested = false;

  private readonly pageVisible = signal(typeof document === 'undefined' || document.visibilityState !== 'hidden');

  private audioCtx: AudioContext | null = null;
  private lastDingAt = 0;

  constructor() {
    document.addEventListener('visibilitychange', () =>
      this.pageVisible.set(document.visibilityState !== 'hidden')
    );

    // El polling solo corre con sesión iniciada y pestaña visible.
    // Al ocultar la pestaña se pausa (sin perder el afterId); al hacer logout se resetea todo.
    effect(() => {
      const authed = this.auth.isAuthenticatedSignal();
      const visible = this.pageVisible();
      untracked(() => {
        if (!authed) {
          this.stopPolling();
          this.resetState();
        } else if (!visible) {
          this.stopPolling();
        } else {
          this.startPolling();
        }
      });
    });
  }

  // ------------------------------------------------------------------ polling

  private startPolling(): void {
    if (this.pollSub) {
      return;
    }
    this.loadPreferencesOnce();
    this.pollSub = timer(0, POLL_INTERVAL_MS).pipe(
      // exhaustMap: nunca solapa peticiones; errores silenciosos para no spamear si el back no responde
      exhaustMap(() => this.pollOnce().pipe(catchError(() => EMPTY)))
    ).subscribe(res => this.applyPoll(res));
  }

  private stopPolling(): void {
    this.pollSub?.unsubscribe();
    this.pollSub = null;
  }

  private resetState(): void {
    this.lastPolledId = null;
    this.unseenCount.set(0);
    this.latestId.set(null);
    this.preferences.set(null);
    this.prefsRequested = false;
  }

  private pollOnce(): Observable<NotificationPollResponse> {
    let params = new HttpParams();
    if (this.lastPolledId !== null) {
      params = params.set('afterId', this.lastPolledId);
    }
    return this.http.get<NotificationPollResponse>(`${this.apiUrl}/poll`, { params });
  }

  private applyPoll(res: NotificationPollResponse): void {
    this.unseenCount.set(res.unseenCount);
    this.latestId.set(res.latestId);

    const isBaseline = this.lastPolledId === null;
    if (res.latestId !== null) {
      this.lastPolledId = res.latestId;
    } else if (isBaseline) {
      // Sin notificaciones todavía: fijamos baseline 0 para que los próximos polls sí traigan events
      this.lastPolledId = 0;
    }
    if (isBaseline) {
      return; // primer poll: solo establece la línea base, sin toasts
    }

    const fresh = res.events.filter(e => !e.seen);
    if (fresh.length > 0) {
      this.pulse.update(v => v + 1);
      for (const event of fresh) {
        this.eventsSubject.next(event);
      }
    }
  }

  // ------------------------------------------------------------------ API REST

  list(offset: number, limit: number, types: string[] = []): Observable<NotificationListResponse> {
    let params = new HttpParams().set('offset', offset).set('limit', limit);
    if (types.length > 0) {
      params = params.set('types', types.join(','));
    }
    return this.http.get<NotificationListResponse>(this.apiUrl, { params });
  }

  markAllSeen(): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/mark-all-seen`, {}).pipe(
      tap(() => this.unseenCount.set(0))
    );
  }

  markSeen(id: number): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/${id}/seen`, {}).pipe(
      tap(() => this.unseenCount.update(c => Math.max(0, c - 1)))
    );
  }

  clearAll(): Observable<void> {
    return this.http.delete<void>(this.apiUrl).pipe(
      tap(() => this.unseenCount.set(0))
    );
  }

  deleteOne(id: number, wasUnseen: boolean): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`).pipe(
      tap(() => {
        if (wasUnseen) {
          this.unseenCount.update(c => Math.max(0, c - 1));
        }
      })
    );
  }

  getPreferences(): Observable<NotificationPreference[]> {
    return this.http.get<NotificationPreference[]>(`${this.apiUrl}/preferences`).pipe(
      tap(list => this.preferences.set(list))
    );
  }

  savePreferences(list: NotificationPreference[]): Observable<NotificationPreference[]> {
    return this.http.put<NotificationPreference[]>(`${this.apiUrl}/preferences`, list).pipe(
      tap(updated => this.preferences.set(updated))
    );
  }

  private loadPreferencesOnce(): void {
    if (this.prefsRequested) {
      return;
    }
    this.prefsRequested = true;
    this.getPreferences().pipe(catchError(() => EMPTY)).subscribe();
  }

  /** Preferencia efectiva de un tipo (valores por defecto si el servidor aún no tiene fila). */
  prefFor(type: string): NotificationPreference {
    return this.preferences()?.find(p => p.type === type) ?? defaultPreference(type);
  }

  // ------------------------------------------------------------------ textos

  /** Traduce una clave e interpola sus {placeholders}. */
  t(key: string, params: Record<string, string | number> = {}): string {
    const stringParams: Record<string, string> = {};
    for (const [k, v] of Object.entries(params)) {
      stringParams[k] = String(v);
    }
    return interpolate(this.language.translate(key), stringParams);
  }

  /** Mensaje humano de una notificación: plantilla i18n + interpolación de params + actorName. */
  messageFor(n: NotificationDTO): string {
    const actorName = n.actorName || this.language.translate('notifications.someone');
    const params: Record<string, string> = { ...n.params, actorName };

    let key = `notifications.msg.${n.type}`;
    if (n.type === 'PARTICIPANT_CHANGED') {
      key += params['action'] === 'REMOVED' ? '.REMOVED' : '.ADDED';
      params['itemKind'] = this.language.translate(`notifications.kind.${params['itemKind'] ?? 'TASK'}`);
    } else if (n.type === 'SUGGESTION_DECIDED') {
      key += params['decision'] === 'REJECTED' ? '.REJECTED' : '.ACCEPTED';
    }

    const template = this.language.translate(key);
    if (template === key) {
      // Tipo desconocido para este front: mostramos algo digno en vez de la clave cruda
      return actorName ? `${actorName} · ${n.type}` : n.type;
    }
    return interpolate(template, params);
  }

  /** Tiempo relativo legible: "ahora mismo", "hace 5 min", "ayer"… (es/en). */
  relativeTime(iso: string, nowMs: number = Date.now()): string {
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) {
      return '';
    }
    const diffMin = Math.floor((nowMs - t) / 60000);
    if (diffMin < 1) {
      return this.t('notifications.time.now');
    }
    if (diffMin < 60) {
      return this.t('notifications.time.minutes', { n: diffMin });
    }
    const diffHours = Math.floor(diffMin / 60);
    if (diffHours < 24) {
      return this.t('notifications.time.hours', { n: diffHours });
    }
    const now = new Date(nowMs);
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    if (t >= startOfToday - 86_400_000) {
      return this.t('notifications.time.yesterday');
    }
    const diffDays = Math.floor((startOfToday - t) / 86_400_000) + 1;
    if (diffDays <= 7) {
      return this.t('notifications.time.days', { n: diffDays });
    }
    const date = new Date(t);
    const locale = this.language.getCurrentLanguage() === 'es' ? 'es-ES' : 'en-US';
    return date.toLocaleDateString(locale, {
      day: 'numeric',
      month: 'short',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  }

  // ------------------------------------------------------------------ sonido

  /**
   * "Ding" corto y agradable generado con WebAudio (dos tonos suaves, sin ficheros).
   * Si el navegador bloquea el audio antes de la primera interacción, falla en silencio.
   */
  playDing(): void {
    const now = Date.now();
    if (now - this.lastDingAt < MIN_MS_BETWEEN_DINGS) {
      return;
    }
    this.lastDingAt = now;
    try {
      this.audioCtx ??= new AudioContext();
      const ctx = this.audioCtx;
      if (ctx.state === 'suspended') {
        void ctx.resume().catch(() => undefined);
      }
      const start = ctx.currentTime;
      const gain = ctx.createGain();
      gain.connect(ctx.destination);
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.2, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.7);

      const tone1 = ctx.createOscillator();
      tone1.type = 'sine';
      tone1.frequency.value = 880; // La5
      const tone2 = ctx.createOscillator();
      tone2.type = 'sine';
      tone2.frequency.value = 1318.5; // Mi6
      tone1.connect(gain);
      tone2.connect(gain);
      tone1.start(start);
      tone2.start(start + 0.09);
      tone1.stop(start + 0.75);
      tone2.stop(start + 0.75);
    } catch {
      // Audio no disponible o bloqueado: no pasa nada
    }
  }
}
